// Core types for Zemer Music

export interface Artist {
  name: string;
  id?: string;
  link?: string;
  thumbnail?: string;
}

export interface Song {
  videoId: string;
  title: string;
  artist: string;
  artists: Artist[];
  thumbnail: string | null;
}

export interface Album {
  albumId: string;
  title: string;
  artist: string;
  thumbnail: string | null;
}

export interface Whitelist {
  artists: Artist[];
}

export interface Playlist {
  id: string;
  name: string;
  songs: Song[];
  cover?: string;
  createdAt: number;
  updatedAt: number;
}

export type RepeatMode = 'off' | 'one' | 'all';

export interface PlaybackState {
  track: Song | null;
  index: number;
  results: Song[];
  position: number;
  timestamp: number;
  isPlaying: boolean;
  repeatMode: RepeatMode;
  shuffleMode: boolean;
}

export interface AppState {
  whitelist: Whitelist | null;
  whitelistedIds: Set<string>;
  currentTab: number;
  queue: Song[];
  currentTrack: Song | null;
  trackIndex: number;
  isPlaying: boolean;
  repeatMode: RepeatMode;
  shuffleMode: boolean;
  playlists: Playlist[];
  favorites: Set<string>;
  recentlyPlayed: Song[];
  searchHistory: string[];
  theme: 'light' | 'dark' | 'auto';
  volume: number;
}

export interface YouTubeSearchResponse {
  results: unknown;
}

export interface YouTubeAlbumResponse {
  results: unknown;
  contents?: unknown;
}

// YouTube Player types
export interface YouTubePlayer {
  loadVideoById(videoId: string, startSeconds?: number): void;
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead?: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  setVolume(volume: number): void;
  getVolume(): number;
  isMuted(): boolean;
  mute(): void;
  unMute(): void;
}

export enum PlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5,
}

// API types
export interface APIClient {
  getWhitelist(): Promise<Whitelist>;
  search(query: string): Promise<YouTubeSearchResponse>;
  browseAlbum(albumId: string): Promise<YouTubeAlbumResponse>;
}

// Storage types
export interface StorageAdapter {
  get<T>(key: string): Promise<T | null>;
  set<T>(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
}

// Error types
export class ZemerError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ZemerError';
  }
}

export interface ErrorInfo {
  message: string;
  code: string;
  timestamp: number;
  context?: Record<string, unknown>;
}
